import { uniqueId } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { getPublicNoticeListAsync } from "src/api/notice/notice-api";
import { Notice, NoticeListParams } from "src/api/notice/notice-types";
import { getPublicSearchProductListAsync, getSearchProductListAsync } from "src/api/product/product-api";
import { ProductListModel, ProductListParams, ProductType, productTypeToString } from "src/api/product/product-types";
import { ResizedImage } from "src/components";
import Footer from "src/components/layout/Footer";
import Header from "src/components/layout/Header";
import Link from "src/components/PartnerLink";
import useIntersectionObserver from "src/pages/hooks/intersection-observer";
import { defaultParams, images } from "src/pages/main/main-types";
import { getKoreanCostUnits, getSalePeriodMessage } from "src/utils/common-util";
import SwiperCore, { Autoplay, Navigation, Pagination, Parallax } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { tabOptions, Tabs } from "./components/Tabs";
import { useMemberInfoState } from "src/recoil/member/hook";
/**
 * 메인 화면
 */

const useProduct = () => {
  const { member } = useMemberInfoState();

  // (인증) 상품목록 api
  const { executeAsync: getProductList, loading: isLoadingProducts } = useApiOperation(getSearchProductListAsync);

  // (무인증) 상품목록 api
  const { executeAsync: getPublicProductList, loading: isLoadingPublicProducts } = useApiOperation(getPublicSearchProductListAsync, {
    noAuthenticationRequired: true,
  });

  return { getProducts: member ? getProductList : getPublicProductList, loading: member ? isLoadingProducts : isLoadingPublicProducts };
};

type Media = { url: string };

interface ProductListModelForFront extends ProductListModel {
  mediaList?: Media[];
}
const isOnlyServePage = process.env.REACT_APP_ONLY_SERVE_PAGE;

const Main = () => {
  SwiperCore.use([Autoplay, Parallax, Navigation, Pagination]);

  // 로그인한 회원 정보
  const { member } = useMemberInfoState();

  const [productList, setProductList] = useState<ProductListModelForFront[]>([]);
  const [noticeList, setNoticeList] = useState<Notice[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedTab, setSelectedTab] = useState<string>("TIME_COURT");
  const location = useLocation();
  const [initialDataFetched, setInitialDataFetched] = useState<boolean>(false);

  const pathname = location.pathname;
  const pathnameArray = pathname.split("/");

  //배열로 만들고 배열안에 "partner"가 있는지 확인
  const partnerIndex = pathnameArray.findIndex((item) => item === "partner");
  //partner가 있으면 그 뒤에 있는 값을 가져오고 없으면 빈 문자열 반환
  const partnerCode = partnerIndex !== -1 ? pathnameArray[partnerIndex + 1] : "";

  const { getProducts, loading } = useProduct();

  // (무인증) 공지사항 목록 api
  const { executeAsync: getPublicNotices } = useApiOperation(getPublicNoticeListAsync, { noAuthenticationRequired: true });

  // 무인증 상품목록 호출
  const fetchProductList = useCallback(
    async (params: ProductListParams) => {
      try {
        const { data, status } = await getProducts({
          ...params,
          ...(member && { memberNo: member?.memberNo }),
          sort: {
            orders: [
              {
                property: "modifiedDate",
                direction: "DESC",
              },
            ],
          },
        });
        if (status >= 200 && status <= 299) {
          const productListData = data?.data?.content;
          setProductList(productListData);
          setTotalPages(Number(data?.meta?.pageMeta?.totalPages || 0));
        }
      } catch (error) {
        setProductList([]);
      } finally {
        setInitialDataFetched(true);
      }
    },
    [getProducts],
  );

  // 공지사항 호출
  const fetchNoticeList = useCallback(
    async (params: NoticeListParams) => {
      const { data, status } = await getPublicNotices(params);
      if (status >= 200 && status <= 299) {
        setNoticeList(data?.data?.rows || []);
      }
    },
    [getPublicNotices],
  );

  // 상품 목록 더 불러오기
  const loadMoreProductList = useCallback(async () => {
    if (productList.length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      let payload = {
        ...defaultParams,
        productType: selectedTab as ProductType,
        ...(member && { memberNo: member?.memberNo }),

        page: nextPage,
        sort: {
          orders: [
            {
              property: "modifiedDate",
              direction: "DESC",
            },
          ],
        },
      };
      if (partnerCode) {
        payload = {
          ...payload,
          ...{ partnerCode: partnerCode },
        };
      }

      const { productType, ...rest } = payload;
      if (!selectedTab) {
        const { data } = await getProducts({ ...rest });
        const productListData = data?.data?.content;
        setProductList([...productList, ...(productListData || [])]);
      } else {
        const { data } = await getProducts(payload);
        const productListData = data?.data?.content;
        setProductList([...productList, ...(productListData || [])]);
      }
    }
  }, [page, productList, getProducts, selectedTab, member]);

  // 스크롤 감지
  const onIntersect: IntersectionObserverCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      // console.log("감지결과 :", entry.isIntersecting);
      if (entry.isIntersecting && !loading && page < totalPages) {
        loadMoreProductList();
      }
    },
    [loadMoreProductList, loading, page, totalPages],
  );

  // 교차 감지 hook (scroll 감지)
  const { setTarget } = useIntersectionObserver({
    onIntersect,
    threshold: [0],
  });

  useEffect(() => {
    let params = { ...defaultParams, productType: "TIME_COURT" };
    if (partnerCode) {
      params = { ...defaultParams, productType: "TIME_COURT", ...{ partnerCode: partnerCode } };
    }
    fetchProductList(params as ProductListParams);
    fetchNoticeList({ ...defaultParams });
  }, [fetchProductList, fetchNoticeList]);

  return (
    <>
      <Header />
      <section className="slider main">
        <Swiper
          pagination={{
            clickable: true, // 버튼 클릭 여부
            dynamicBullets: true,
            dynamicMainBullets: 3,
          }}
          autoplay={{
            delay: 2500,
            disableOnInteraction: false,
          }}
          watchOverflow={true}
          parallax={true}
          className="main-swiper"
        >
          {images.map((item, i) => {
            return (
              <SwiperSlide key={item.pic + i}>
                <div className="slider-bg" data-swiper-parallax={"23%"}>
                  <ResizedImage url={item.pic} noResize={true} className="slider-bg-img" alt={`메인이미지-${item.title}-${i}`} />
                  <h1 className="main-slider-box-title" data-swiper-parallax="-300">
                    {item.title}
                  </h1>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>
      {noticeList.length > 0 ? (
        <a href={`/front/space/notices/${noticeList?.[0]?.id}?page=public`}>
          <section className="notice-area">
            <span>공지</span>
            <p>{noticeList?.[0]?.title || "-"}</p>
          </section>
        </a>
      ) : (
        <section className="notice-area">
          <span>공지</span>
          <p>-</p>
        </section>
      )}

      <article className="main-contents">
        <section className="main-contents__top">
          <h2>어떤 공간을 찾으시나요?</h2>
          <Tabs
            tabs={tabOptions}
            onSelected={(selected) => {
              setPage(0);
              setSelectedTab(selected);
              // setProductList([]);
              const payload = selected
                ? {
                  ...defaultParams,
                  productType: selected as ProductType,
                  ...(partnerCode && { partnerCode }),
                }
                : { ...defaultParams, ...(partnerCode && { partnerCode }) };
              fetchProductList(payload);
            }}
          />
        </section>
        <section className="space-product">
          <ul>
            {productList?.map((product: ProductListModelForFront, index: number) => {
              // 판매시작날짜가 있으면 > 시작 날짜가 오늘보다 이전일 때 판매 가능
              // 판매종료날짜가 있으면 > 종료 날짜와 시간이 현재보다 이후일 때 종료
              // 판매시작 / 판매 종료 데이터가 없으면 상시판매

              const { title, message, isAvailableSaleStartDate, isAvailableSaleEndDate } = getSalePeriodMessage(product);
              return (
                <Link to={`/court/product/${product.id}`} key={uniqueId()}>
                  <li>
                    <div className={`slider list ${!product?.mediaList || product?.mediaList?.length === 0 ? "noImage" : ""}`}>
                      <Swiper
                        pagination={{
                          clickable: true, // 버튼 클릭 여부
                          dynamicBullets: true,
                          dynamicMainBullets: 3,
                        }}
                        watchOverflow={true}
                        parallax={true}
                        className="list-swiper"
                      >
                        <SwiperSlide>
                          <div
                            className={`slider-bg ${!isAvailableSaleStartDate || !isAvailableSaleEndDate ? "unavailable" : ""}`}
                            data-swiper-parallax={"23%"}
                          >
                            <ResizedImage
                              url={product.primaryImageUrl}
                              className="slider-bg-img "
                              alt={`상품이미지-${product.productName}-${index}`}
                            />
                          </div>

                          {(!isAvailableSaleStartDate || !isAvailableSaleEndDate) && (
                            <div className="main-slider-box-message ">
                              <p className="inavlable-title" data-swiper-parallax="100">
                                {/* 판매 기간이 아닙니다. */}
                                {title}
                              </p>
                              <p className="inavlable-message" data-swiper-parallax="100">
                                {message}
                              </p>
                            </div>
                          )}
                        </SwiperSlide>
                      </Swiper>
                    </div>

                    <div className="information">
                      <div className="inline-wrap">
                        <div className={`space-chip mr8 ${product.productType === "TIME_COURT" ? "color-white" : ""}`}>
                          <span>{productTypeToString(product.productType)}</span>
                        </div>
                        <h2>{product.productName}</h2>
                      </div>
                      <p className="desc">{product?.introduce}</p>
                      <div className="flex-center">
                        <p className="info ic-place">{product?.buildingAddressSummary}</p>
                        {product.productType === "FULL_COURT" ? (
                          <p className="info ic-size ml10">
                            임대 {product?.leasableArea}㎡ / 전용 {product?.leasableAreaNet}㎡
                          </p>
                        ) : (
                          <>{product?.facilityMaxPeopleNums && <p className="info ic-users ml10">최대 {product?.facilityMaxPeopleNums}명</p>}</>
                        )}
                      </div>
                      {!isOnlyServePage && (
                        <>
                          {product.productType === "FULL_COURT" ? (
                            <div className="price-info-container">
                              <div className="price-info">
                                <p className="price">{getKoreanCostUnits(product?.deposit).cost}</p>
                                <p className="text">
                                  {getKoreanCostUnits(product?.deposit).unit ? `${getKoreanCostUnits(product?.deposit).unit}~` : "원~"}{" "}
                                  <span>/보증금</span>
                                </p>
                              </div>
                              <p className="vertical-divider"></p>
                              <div className="price-info">
                                <p className="price">{getKoreanCostUnits(product?.priceMin).cost}</p>
                                <p className="text">
                                  {getKoreanCostUnits(product?.priceMin).unit ? `${getKoreanCostUnits(product?.priceMin).unit}~` : "원~"}
                                  <span>/1개월</span>
                                </p>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="price-info-container">
                                <div className="price-info">
                                  <p className="price">{getKoreanCostUnits(product?.priceMin).cost}</p>
                                  <p className="text">
                                    {getKoreanCostUnits(product?.priceMin).unit ? `${getKoreanCostUnits(product?.priceMin).unit}~` : "원~"}
                                    <span>/1시간</span>
                                  </p>
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                </Link>
              );
            })}
          </ul>
        </section>
        {initialDataFetched && !loading && productList?.length === 0 && (
          <div className="no-data">
            <div className="error-icon ic-none"></div>
            <p>등록된 상품이 없습니다</p>
          </div>
        )}
      </article>
      <div ref={setTarget}></div>
      <Footer />
    </>
  );
};
export default Main;
